// Reservation Service for Hotel A.S.K Grand
// Handles future bookings, current occupancy, and availability checking

import { ref, set, get, push, update } from 'firebase/database';
import { database } from './firebase';

export interface Reservation {
  id: string;
  roomNumber: string;
  guestName: string;
  guestPhone: string;
  guestEmail: string;
  checkIn: string; // YYYY-MM-DD
  checkOut: string; // YYYY-MM-DD
  guests: number;
  idProofType?: string;
  idProofNumber?: string;
  specialRequests?: string;
  customPrice?: number;
  status: 'confirmed' | 'checked-in' | 'checked-out' | 'cancelled';
  paymentStatus: 'paid' | 'pending' | 'partial';
  createdAt: string;
  updatedAt: string;
}

// Check if a room is available for given dates
export async function checkRoomAvailability(
  roomNumber: string,
  checkIn: string,
  checkOut: string,
  excludeReservationId?: string
): Promise<boolean> {
  try {
    const reservationsRef = ref(database, 'reservations');
    const snapshot = await get(reservationsRef);
    
    if (!snapshot.exists()) {
      return true; // No reservations, room is available
    }

    const allReservations = snapshot.val();
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);

    // Check for conflicts
    for (const [id, reservation] of Object.entries(allReservations)) {
      const res = reservation as Reservation;
      
      // Skip if it's the same reservation we're updating
      if (excludeReservationId && id === excludeReservationId) continue;
      
      // Skip cancelled reservations
      if (res.status === 'cancelled') continue;
      
      // Only check reservations for the same room
      if (res.roomNumber !== roomNumber) continue;

      const resCheckIn = new Date(res.checkIn);
      const resCheckOut = new Date(res.checkOut);

      // Check for date overlap
      // Overlap occurs if: new check-in < existing check-out AND new check-out > existing check-in
      if (checkInDate < resCheckOut && checkOutDate > resCheckIn) {
        return false; // Conflict found
      }
    }

    return true; // No conflicts
  } catch (error) {
    console.error('Error checking availability:', error);
    return false;
  }
}

// Get all reservations for a room
export async function getRoomReservations(roomNumber: string): Promise<Reservation[]> {
  try {
    const reservationsRef = ref(database, 'reservations');
    const snapshot = await get(reservationsRef);
    
    if (!snapshot.exists()) {
      return [];
    }

    const allReservations = snapshot.val();
    const roomReservations: Reservation[] = [];

    for (const [id, reservation] of Object.entries(allReservations)) {
      const res = reservation as Reservation;
      if (res.roomNumber === roomNumber && res.status !== 'cancelled') {
        roomReservations.push({ ...res, id });
      }
    }

    // Sort by check-in date
    return roomReservations.sort((a, b) => 
      new Date(a.checkIn).getTime() - new Date(b.checkIn).getTime()
    );
  } catch (error) {
    console.error('Error getting room reservations:', error);
    return [];
  }
}

// Get current guest (checked-in reservation)
export async function getCurrentGuest(roomNumber: string): Promise<Reservation | null> {
  try {
    const reservations = await getRoomReservations(roomNumber);
    const today = new Date().toISOString().split('T')[0];
    
    // Find reservation where today is between check-in and check-out
    const currentGuest = reservations.find(res => 
      res.status === 'checked-in' &&
      res.checkIn <= today &&
      res.checkOut > today
    );

    return currentGuest || null;
  } catch (error) {
    console.error('Error getting current guest:', error);
    return null;
  }
}

// Get upcoming reservations for a room
export async function getUpcomingReservations(roomNumber: string): Promise<Reservation[]> {
  try {
    const reservations = await getRoomReservations(roomNumber);
    const today = new Date().toISOString().split('T')[0];
    
    return reservations.filter(res => 
      res.status === 'confirmed' &&
      res.checkIn > today
    );
  } catch (error) {
    console.error('Error getting upcoming reservations:', error);
    return [];
  }
}

// Create a new reservation
export async function createReservation(reservationData: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'>): Promise<string | null> {
  try {
    // Check availability first
    const isAvailable = await checkRoomAvailability(
      reservationData.roomNumber,
      reservationData.checkIn,
      reservationData.checkOut
    );

    if (!isAvailable) {
      throw new Error('Room is not available for selected dates');
    }

    const reservationsRef = ref(database, 'reservations');
    const newReservationRef = push(reservationsRef);
    const reservationId = newReservationRef.key!;

    const reservation: Reservation = {
      ...reservationData,
      id: reservationId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    await set(newReservationRef, reservation);
    
    return reservationId;
  } catch (error) {
    console.error('Error creating reservation:', error);
    throw error;
  }
}

// Update reservation status (e.g., check-in, check-out)
export async function updateReservationStatus(
  reservationId: string,
  status: Reservation['status']
): Promise<boolean> {
  try {
    const reservationRef = ref(database, `reservations/${reservationId}`);
    await update(reservationRef, {
      status,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating reservation status:', error);
    return false;
  }
}

// Cancel a reservation
export async function cancelReservation(reservationId: string): Promise<boolean> {
  try {
    return await updateReservationStatus(reservationId, 'cancelled');
  } catch (error) {
    console.error('Error cancelling reservation:', error);
    return false;
  }
}

// Get room status for a specific date
export async function getRoomStatusForDate(roomNumber: string, date: string): Promise<'available' | 'occupied' | 'reserved'> {
  try {
    const reservations = await getRoomReservations(roomNumber);
    
    for (const res of reservations) {
      if (date >= res.checkIn && date < res.checkOut) {
        return res.status === 'checked-in' ? 'occupied' : 'reserved';
      }
    }
    
    return 'available';
  } catch (error) {
    console.error('Error getting room status for date:', error);
    return 'available';
  }
}

// Get available date ranges for a room (next 90 days)
export async function getAvailableDateRanges(roomNumber: string): Promise<Array<{start: string, end: string}>> {
  try {
    const reservations = await getRoomReservations(roomNumber);
    const today = new Date();
    const ranges: Array<{start: string, end: string}> = [];
    
    // Sort reservations by check-in date
    const sortedReservations = reservations.sort((a, b) => 
      new Date(a.checkIn).getTime() - new Date(b.checkIn).getTime()
    );

    let currentDate = today.toISOString().split('T')[0];
    
    for (const res of sortedReservations) {
      if (res.checkIn > currentDate) {
        // There's a gap - add available range
        ranges.push({
          start: currentDate,
          end: res.checkIn
        });
      }
      currentDate = res.checkOut;
    }
    
    // Add final range (from last checkout to 90 days from now)
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 90);
    ranges.push({
      start: currentDate,
      end: maxDate.toISOString().split('T')[0]
    });
    
    return ranges;
  } catch (error) {
    console.error('Error getting available date ranges:', error);
    return [];
  }
}

// Get all reservations (for admin dashboard)
export async function getAllReservations(): Promise<Reservation[]> {
  try {
    const reservationsRef = ref(database, 'reservations');
    const snapshot = await get(reservationsRef);
    
    if (!snapshot.exists()) {
      return [];
    }

    const allReservations = snapshot.val();
    const reservationsList: Reservation[] = [];

    for (const [id, reservation] of Object.entries(allReservations)) {
      reservationsList.push({ ...(reservation as Reservation), id });
    }

    return reservationsList.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  } catch (error) {
    console.error('Error getting all reservations:', error);
    return [];
  }
}

// Subscribe to reservations in real-time
export function subscribeToReservations(callback: (reservations: Reservation[]) => void) {
  const reservationsRef = ref(database, 'reservations');
  
  return get(reservationsRef).then(snapshot => {
    if (snapshot.exists()) {
      const allReservations = snapshot.val();
      const reservationsList: Reservation[] = [];
      
      for (const [id, reservation] of Object.entries(allReservations)) {
        reservationsList.push({ ...(reservation as Reservation), id });
      }
      
      callback(reservationsList);
    } else {
      callback([]);
    }
  });
}
